import pLimit from "p-limit";

import { encodeKey, FileItem, isDirectory } from "../FileGrid";
import {
  findMoveConflicts,
  MoveConflict,
  MoveConflictPolicy,
  MoveProgress,
  MoveResult,
  MoveSource,
  normalizeDirectory,
  targetForSource,
  validateMoveDestination,
} from "./move";
import { TransferTask } from "./transferQueue";

const WEBDAV_ENDPOINT = "/webdav/";

export async function fetchPath(
  path: string,
  depth = "1",
  options?: { onReadonly?: (readonly: boolean) => void }
) {
  const res = await fetch(`${WEBDAV_ENDPOINT}${encodeKey(path)}`, {
    method: "PROPFIND",
    headers: { Depth: depth },
  });

  if (!res.ok) throw new Error("Failed to fetch");
  if (!res.headers.get("Content-Type")?.includes("application/xml"))
    throw new Error("Invalid response");
  options?.onReadonly?.(res.headers.get("fd-readonly") === "1");

  const parser = new DOMParser();
  const text = await res.text();
  const document = parser.parseFromString(text, "application/xml");
  const items: FileItem[] = Array.from(document.querySelectorAll("response"))
    .filter(
      (response) =>
        decodeURIComponent(
          response.querySelector("href")?.textContent ?? ""
        ).slice(WEBDAV_ENDPOINT.length) !== path.replace(/\/$/, "")
    )
    .map((response) => {
      const href = response.querySelector("href")?.textContent;
      if (!href) throw new Error("Invalid response");
      const contentType = response.querySelector("getcontenttype")?.textContent;
      const size = response.querySelector("getcontentlength")?.textContent;
      const lastModified =
        response.querySelector("getlastmodified")?.textContent;
      const thumbnail = response.getElementsByTagNameNS(
        "flaredrive",
        "thumbnail"
      )[0]?.textContent;
      return {
        key: decodeURI(href).replace(/^\/webdav\//, ""),
        size: size ? Number(size) : 0,
        uploaded: lastModified!,
        httpMetadata: { contentType: contentType! },
        customMetadata: { thumbnail },
      } as FileItem;
    });
  return items;
}

const THUMBNAIL_SIZE = 144;

export async function generateThumbnail(file: File) {
  const canvas = document.createElement("canvas");
  canvas.width = THUMBNAIL_SIZE;
  canvas.height = THUMBNAIL_SIZE;
  var ctx = canvas.getContext("2d")!;

  if (file.type.startsWith("image/")) {
    const image = await new Promise<HTMLImageElement>((resolve) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.src = URL.createObjectURL(file);
    });
    ctx.drawImage(image, 0, 0, THUMBNAIL_SIZE, THUMBNAIL_SIZE);
  } else if (file.type === "video/mp4") {
    // Generate thumbnail from video
    const video = await new Promise<HTMLVideoElement>(
      async (resolve, reject) => {
        const video = document.createElement("video");
        video.muted = true;
        video.src = URL.createObjectURL(file);
        setTimeout(() => reject(new Error("Video load timeout")), 2000);
        await video.play();
        video.pause();
        video.currentTime = 0;
        resolve(video);
      }
    );
    ctx.drawImage(video, 0, 0, THUMBNAIL_SIZE, THUMBNAIL_SIZE);
  } else if (file.type === "application/pdf") {
    const pdfjsLib = await import(
      /* webpackIgnore: true */
      // @ts-ignore
      "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.4.168/pdf.min.mjs"
    );
    pdfjsLib.GlobalWorkerOptions.workerSrc =
      "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.4.168/pdf.worker.min.mjs";
    const pdf = await pdfjsLib.getDocument(URL.createObjectURL(file)).promise;
    const page = await pdf.getPage(1);
    const { width, height } = page.getViewport({ scale: 1 });
    var scale = THUMBNAIL_SIZE / Math.max(width, height);
    const viewport = page.getViewport({ scale });
    const renderContext = { canvasContext: ctx, viewport };
    await page.render(renderContext).promise;
  }

  const thumbnailBlob = await new Promise<Blob>((resolve) =>
    canvas.toBlob((blob) => resolve(blob!))
  );

  return thumbnailBlob;
}

export async function blobDigest(blob: Blob) {
  const digest = await crypto.subtle.digest("SHA-1", await blob.arrayBuffer());
  const digestArray = Array.from(new Uint8Array(digest));
  const digestHex = digestArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return digestHex;
}

export const SIZE_LIMIT = 100 * 1000 * 1000; // 100MB

function xhrFetch(
  url: RequestInfo | URL,
  requestInit: RequestInit & {
    onUploadProgress?: (progressEvent: ProgressEvent) => void;
  }
) {
  return new Promise<Response>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.onprogress = requestInit.onUploadProgress ?? null;
    xhr.open(
      requestInit.method ?? "GET",
      url instanceof Request ? url.url : url
    );
    const headers = new Headers(requestInit.headers);
    headers.forEach((value, key) => xhr.setRequestHeader(key, value));
    xhr.onload = () => {
      const headers = xhr
        .getAllResponseHeaders()
        .trim()
        .split("\r\n")
        .reduce((acc, header) => {
          const [key, value] = header.split(": ");
          acc[key] = value;
          return acc;
        }, {} as Record<string, string>);
      resolve(new Response(xhr.responseText, { status: xhr.status, headers }));
    };
    xhr.onerror = reject;
    if (
      requestInit.body instanceof Blob ||
      typeof requestInit.body === "string"
    ) {
      xhr.send(requestInit.body);
    }
  });
}

export async function multipartUpload(
  key: string,
  file: File,
  options?: {
    headers?: Record<string, string>;
    onUploadProgress?: (progressEvent: {
      loaded: number;
      total: number;
    }) => void;
  }
) {
  const headers = options?.headers || {};
  headers["content-type"] = file.type;

  const uploadResponse = await fetch(`/webdav/${encodeKey(key)}?uploads`, {
    headers,
    method: "POST",
  });
  const { uploadId } = await uploadResponse.json<{ uploadId: string }>();
  const totalChunks = Math.ceil(file.size / SIZE_LIMIT);

  const limit = pLimit(2);
  const parts = Array.from({ length: totalChunks }, (_, i) => i + 1);
  const partsLoaded = Array.from({ length: totalChunks + 1 }, () => 0);
  const promises = parts.map((i) =>
    limit(async () => {
      const chunk = file.slice((i - 1) * SIZE_LIMIT, i * SIZE_LIMIT);
      const searchParams = new URLSearchParams({
        partNumber: i.toString(),
        uploadId,
      });
      const uploadUrl = `/webdav/${encodeKey(key)}?${searchParams}`;
      if (i === limit.concurrency)
        await new Promise((resolve) => setTimeout(resolve, 1000));

      const uploadPart = () =>
        xhrFetch(uploadUrl, {
          method: "PUT",
          headers,
          body: chunk,
          onUploadProgress: (progressEvent) => {
            partsLoaded[i] = progressEvent.loaded;
            options?.onUploadProgress?.({
              loaded: partsLoaded.reduce((a, b) => a + b),
              total: file.size,
            });
          },
        });

      const retryReducer = (acc: Promise<Response>) =>
        acc
          .then((res) => {
            const retryAfter = res.headers.get("retry-after");
            if (!retryAfter) return res;
            return uploadPart();
          })
          .catch(uploadPart);
      const response = await [1, 2].reduce(retryReducer, uploadPart());
      return { partNumber: i, etag: response.headers.get("etag")! };
    })
  );
  const uploadedParts = await Promise.all(promises);
  const completeParams = new URLSearchParams({ uploadId });
  const response = await fetch(`/webdav/${encodeKey(key)}?${completeParams}`, {
    method: "POST",
    body: JSON.stringify({ parts: uploadedParts }),
  });
  if (!response.ok) throw new Error(await response.text());
  return response;
}

export async function copyPaste(
  source: string,
  target: string,
  move = false,
  overwrite = true
) {
  const uploadUrl = `${WEBDAV_ENDPOINT}${encodeKey(source)}`;
  const destinationUrl = new URL(
    `${WEBDAV_ENDPOINT}${encodeKey(target)}`,
    window.location.href
  );
  const response = await fetch(uploadUrl, {
    method: move ? "MOVE" : "COPY",
    headers: {
      Destination: destinationUrl.href,
      ...(overwrite ? {} : { Overwrite: "F" }),
    },
  });
  if (!response.ok) throw new Error(await response.text());
  return response;
}

export async function createRemoteFolder(key: string) {
  const response = await fetch(`${WEBDAV_ENDPOINT}${encodeKey(key)}`, {
    method: "MKCOL",
  });
  if (!response.ok) throw new Error(await response.text());
}

export async function deletePath(key: string) {
  const response = await fetch(`${WEBDAV_ENDPOINT}${encodeKey(key)}`, {
    method: "DELETE",
  });
  if (!response.ok) throw new Error(await response.text());
}

export type RenameProgress = {
  done: number;
  total: number;
  phase: string;
  current?: string;
};

type MoveTarget = {
  source: MoveSource;
  target: string;
};

type MoveWork = MoveTarget & {
  folders: Array<{ source: string; target: string }>;
  files: Array<{ source: string; target: string }>;
};

function moveError(error: unknown) {
  return error instanceof Error ? error : new Error(String(error));
}

async function buildMoveWork({ source, target }: MoveTarget): Promise<MoveWork> {
  if (!source.isDirectory) {
    return {
      source,
      target,
      folders: [],
      files: [{ source: source.key, target }],
    };
  }

  const children = (await fetchPath(source.key, "infinity")).sort((a, b) =>
    a.key.localeCompare(b.key)
  );
  const targetForChild = (key: string) =>
    `${target}${key.slice(source.key.length)}`;

  return {
    source,
    target,
    folders: [
      { source: source.key, target },
      ...children
        .filter(isDirectory)
        .sort(
          (a, b) => a.key.split("/").length - b.key.split("/").length
        )
        .map((item) => ({
          source: item.key,
          target: targetForChild(item.key),
        })),
    ],
    files: children
      .filter((item) => !isDirectory(item))
      .map((item) => ({
        source: item.key,
        target: targetForChild(item.key),
      })),
  };
}

async function executeMoveTargets({
  moves,
  conflictPolicy,
  conflicts,
  onProgress,
  overwriteCopies,
}: {
  moves: MoveTarget[];
  conflictPolicy: MoveConflictPolicy;
  conflicts: MoveConflict[];
  onProgress?: (progress: MoveProgress) => void;
  overwriteCopies: boolean;
}): Promise<MoveResult> {
  const result: MoveResult = { succeeded: [], skipped: [], failed: [] };
  const conflictTargets = new Map(
    conflicts.map((conflict) => [conflict.source.key, conflict.target])
  );
  const skipped =
    conflictPolicy === "skip"
      ? new Set(conflicts.map((conflict) => conflict.source.key))
      : new Set<string>();
  result.skipped.push(
    ...moves
      .filter(({ source }) => skipped.has(source.key))
      .map(({ source }) => source.key)
  );

  let completedItems = result.skipped.length;
  const totalItems = moves.length;
  onProgress?.({
    done: 0,
    total: 0,
    phase: "counting",
    completedItems,
    totalItems,
  });

  const work: MoveWork[] = [];
  for (const move of moves) {
    if (skipped.has(move.source.key)) continue;
    try {
      work.push(await buildMoveWork(move));
    } catch (error) {
      result.failed.push({ source: move.source.key, error: moveError(error) });
      completedItems += 1;
    }
  }

  const overwriteUnits =
    conflictPolicy === "overwrite"
      ? work.filter(({ source }) => conflictTargets.has(source.key)).length
      : 0;
  const total =
    overwriteUnits +
    work.reduce(
      (sum, item) => sum + item.folders.length + item.files.length + 1,
      0
    );
  let done = 0;
  const report = (phase: MoveProgress["phase"], current?: string) =>
    onProgress?.({
      done,
      total,
      phase,
      current,
      completedItems,
      totalItems,
    });
  const advance = (phase: MoveProgress["phase"], current: string) => {
    done += 1;
    report(phase, current);
  };

  for (const item of work) {
    try {
      if (
        conflictPolicy === "overwrite" &&
        conflictTargets.has(item.source.key)
      ) {
        report("overwriting", item.target);
        await deletePath(item.target);
        advance("overwriting", item.target);
      }

      for (const folder of item.folders) {
        report("copying", folder.source);
        await createRemoteFolder(folder.target);
        advance("copying", folder.source);
      }

      const limit = pLimit(5);
      const copyResults = await Promise.allSettled(
        item.files.map((file) =>
          limit(async () => {
            await copyPaste(file.source, file.target, false, overwriteCopies);
            advance("copying", file.source);
          })
        )
      );
      const failedCopy = copyResults.find(
        (copyResult): copyResult is PromiseRejectedResult =>
          copyResult.status === "rejected"
      );
      if (failedCopy) throw failedCopy.reason;

      report("deleting", item.source.key);
      await deletePath(item.source.key);
      advance("deleting", item.source.key);
      result.succeeded.push(item.source.key);
      completedItems += 1;
    } catch (error) {
      result.failed.push({
        source: item.source.key,
        error: moveError(error),
      });
      completedItems += 1;
    }
  }

  onProgress?.({
    done: total,
    total,
    phase: "done",
    completedItems,
    totalItems,
  });
  return result;
}

export async function prepareMove(
  sources: MoveSource[],
  destination: string
) {
  const invalid = validateMoveDestination(sources, destination);
  if (invalid.length) {
    throw new Error(`Invalid move destination: ${invalid.join(", ")}`);
  }
  const destinationItems = await fetchPath(normalizeDirectory(destination));
  return findMoveConflicts(sources, destination, destinationItems);
}

export async function movePaths({
  sources,
  destination,
  conflictPolicy,
  conflicts,
  onProgress,
}: {
  sources: MoveSource[];
  destination: string;
  conflictPolicy: MoveConflictPolicy;
  conflicts?: MoveConflict[];
  onProgress?: (progress: MoveProgress) => void;
}) {
  const invalid = validateMoveDestination(sources, destination);
  if (invalid.length) {
    throw new Error(`Invalid move destination: ${invalid.join(", ")}`);
  }
  const resolvedConflicts =
    conflicts ?? (await prepareMove(sources, destination));
  return executeMoveTargets({
    moves: sources.map((source) => ({
      source,
      target: targetForSource(source.key, destination),
    })),
    conflictPolicy,
    conflicts: resolvedConflicts,
    onProgress,
    overwriteCopies: false,
  });
}

export async function renamePath({
  source,
  target,
  sourceIsDirectory,
  onProgress,
  onMoveProgress,
}: {
  source: string;
  target: string;
  sourceIsDirectory: boolean;
  onProgress?: (progress: RenameProgress) => void;
  onMoveProgress?: (progress: MoveProgress) => void;
}) {
  const phaseLabels: Record<MoveProgress["phase"], string> = {
    counting: "Counting files",
    overwriting: "Overwriting existing",
    copying: "Copying files",
    deleting: "Deleting original",
    done: "Done",
  };
  const result = await executeMoveTargets({
    moves: [
      {
        source: { key: source, isDirectory: sourceIsDirectory },
        target,
      },
    ],
    conflictPolicy: "skip",
    conflicts: [],
    overwriteCopies: true,
    onProgress: (progress) => {
      onMoveProgress?.(progress);
      onProgress?.({
        done: progress.done,
        total: progress.total || 1,
        phase: phaseLabels[progress.phase],
        current: progress.phase === "done" ? source : progress.current,
      });
    },
  });
  if (result.failed.length) throw result.failed[0].error;
}

export async function createFolder(cwd: string) {
  try {
    const folderName = window.prompt("Folder name");
    if (!folderName) return;
    if (folderName.includes("/")) {
      window.alert("Invalid folder name");
      return;
    }
    const folderKey = `${cwd}${folderName}`;
    const uploadUrl = `${WEBDAV_ENDPOINT}${encodeKey(folderKey)}`;
    await fetch(uploadUrl, { method: "MKCOL" });
  } catch (error) {
    console.log(`Create folder failed`);
  }
}

export async function processTransferTask({
  task,
  onTaskProgress,
}: {
  task: TransferTask;
  onTaskProgress?: (event: { loaded: number; total: number }) => void;
}) {
  const { remoteKey, file } = task;
  if (task.type !== "upload" || !file) throw new Error("Invalid task");
  let thumbnailDigest = null;

  if (
    file.type.startsWith("image/") ||
    file.type === "video/mp4" ||
    file.type === "application/pdf"
  ) {
    try {
      const thumbnailBlob = await generateThumbnail(file);
      const digestHex = await blobDigest(thumbnailBlob);

      const thumbnailUploadUrl = `/webdav/_$flaredrive$/thumbnails/${digestHex}.png`;
      try {
        await fetch(thumbnailUploadUrl, {
          method: "PUT",
          body: thumbnailBlob,
        });
        thumbnailDigest = digestHex;
      } catch (error) {
        console.log(`Upload ${digestHex}.png failed`);
      }
    } catch (error) {
      console.log(`Generate thumbnail failed`);
    }
  }

  const headers: { "fd-thumbnail"?: string } = {};
  if (thumbnailDigest) headers["fd-thumbnail"] = thumbnailDigest;
  if (file.size >= SIZE_LIMIT) {
    return await multipartUpload(remoteKey, file, {
      headers,
      onUploadProgress: onTaskProgress,
    });
  } else {
    const uploadUrl = `${WEBDAV_ENDPOINT}${encodeKey(remoteKey)}`;
    return await xhrFetch(uploadUrl, {
      method: "PUT",
      headers,
      body: file,
      onUploadProgress: onTaskProgress,
    });
  }
}
